import React, { useState, useCallback, useEffect, memo } from 'react';

let _setToast = null;

/**
 * Canal único de feedback de ação.
 *
 * O app tinha três caminhos concorrentes: `div.msg` no topo do card (com cinco
 * timeouts diferentes), este toast e `window.alert`. Na mesma tela, salvar
 * avisava em um lugar e excluir em outro. A regra agora é: resultado de ação do
 * usuário vai para o toast; erro de validação de um campo fica junto do campo.
 */
export function toast(mensagem, tipo = 'success') {
  if (_setToast) _setToast({ mensagem, tipo, id: Date.now() + Math.random() });
}

const ICONES = { success: '✓', error: '✕', info: 'ℹ' };
const CORES = {
  success: { bg: '#f0fff4', border: '#9ae6b4', color: '#276749' },
  error:   { bg: '#fff5f5', border: '#feb2b2', color: '#c53030' },
  info:    { bg: '#ebf8ff', border: '#90cdf4', color: '#2b6cb0' },
};

// Erro fica mais tempo na tela: costuma trazer texto para ler.
const DURACAO = { success: 3200, info: 4000, error: 6000 };

const ToastItem = memo(function ToastItem({ mensagem, tipo, onRemove }) {
  const c = CORES[tipo] || CORES.info;
  useEffect(() => {
    const t = setTimeout(onRemove, DURACAO[tipo] || 3500);
    return () => clearTimeout(t);
  }, [onRemove, tipo]);

  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
        background: c.bg, border: `1px solid ${c.border}`, color: c.color,
        borderRadius: 'var(--radius-sm)', padding: '0.75rem 1rem',
        boxShadow: 'var(--shadow-md)', fontSize: '0.875rem', fontWeight: 500,
        animation: 'toastIn 0.2s ease', lineHeight: 1.45,
      }}
    >
      <span style={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }} aria-hidden="true">{ICONES[tipo]}</span>
      <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word' }}>{mensagem}</span>
      <button
        onClick={onRemove}
        aria-label="Fechar aviso"
        style={{
          background: 'none', border: 'none', color: c.color, cursor: 'pointer',
          padding: '0 0 0 0.25rem', minHeight: 'unset', boxShadow: 'none',
          fontSize: '0.85rem', opacity: 0.6, flexShrink: 0,
        }}
      >
        <span aria-hidden="true">✕</span>
      </button>
    </div>
  );
});

export default function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  _setToast = useCallback((t) => setToasts(prev => [...prev.slice(-2), t]), []);

  const remover = useCallback((id) => setToasts(prev => prev.filter(t => t.id !== id)), []);

  if (toasts.length === 0) return null;

  return (
    // role=status faz o leitor de tela anunciar sem roubar o foco.
    <div
      role="status"
      aria-live="polite"
      className="toast-container"
    >
      {toasts.map(t => (
        <ToastItem key={t.id} mensagem={t.mensagem} tipo={t.tipo} onRemove={() => remover(t.id)} />
      ))}
    </div>
  );
}
